import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronRight,
  FaPlus,
  FaTrash,
  FaListUl,
  FaUser,
  FaEdit,
  FaUsers,
} from "react-icons/fa";
import { getUserId } from "../utils/userUtils";
import {
  listUserThreads,
  createThread,
  deleteThread,
} from "../utils/threadUtils";
import {
  listUserCharacters,
  createCharacter,
  deleteCharacter,
  updateCharacter,
  isCustomCharacter,
  getCharacterDisplayName,
  getUserOpenedCharacters,
} from "../utils/characterUtils";
import CharacterCreationForm from "./CharacterCreationForm";
import CharacterEditForm from "./CharacterEditForm";

// Helper functions for localStorage
const saveLastVisitedThread = (botId, threadId) => {
  localStorage.setItem(
    "lastVisitedThread",
    JSON.stringify({ botId, threadId })
  );
  console.log(
    `Saved last visited thread: botId=${botId}, threadId=${threadId}`
  );
};

export const getLastVisitedThread = () => {
  const savedThread = localStorage.getItem("lastVisitedThread");
  if (savedThread) {
    try {
      return JSON.parse(savedThread);
    } catch (error) {
      console.error("Error parsing saved thread:", error);
      return null;
    }
  }
  return null;
};

export default function Sidebar({
  user,
  subscribedProducts = [],
  userOpenedCharacters = [],
  setSelectedChatBotId,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [userThreads, setUserThreads] = useState({});
  const [expandedBots, setExpandedBots] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [creatingThread, setCreatingThread] = useState(null);
  const [userCharacters, setUserCharacters] = useState([]);
  const [openedCharacters, setOpenedCharacters] = useState([]);
  const [showCharacterForm, setShowCharacterForm] = useState(false);
  const [creatingCharacter, setCreatingCharacter] = useState(false);
  const [deletingCharacter, setDeletingCharacter] = useState(null);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [updatingCharacter, setUpdatingCharacter] = useState(false);
  const [notification, setNotification] = useState(null);
  const sidebarRef = useRef(null);
  const buttonRef = useRef(null);

  // Chatbot names mapping
  const chatbotNames = {
    1: "Samantha",
  };

  // Add chatbot images mapping
  const chatbotImages = {
    1: "/sam_ai_sapiens_small.jpg",
  };

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    // Clear any existing notification first
    setNotification(null);
    
    // Set new notification
    setTimeout(() => {
      setNotification({ message, type });
    }, 100);
    
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setNotification(null);
    }, 4100);
  };

  // Fetch threads when the user changes
  const fetchThreads = async () => {
    if (user) {
      setIsLoading(true);
      try {
        const userId = getUserId(user);
        console.log('🔍 DEBUG: Fetching threads for user:', userId);
        console.log('🔍 DEBUG: User object:', user);
        
        if (userId) {
          const threads = await listUserThreads(userId);
          console.log('🔍 DEBUG: Retrieved threads:', threads);

          // Organize threads by bot_id
          const threadsByBot = {};
          threads.forEach((thread) => {
            console.log('🔍 DEBUG: Processing thread:', thread);
            if (!threadsByBot[thread.bot_id]) {
              threadsByBot[thread.bot_id] = [];
            }
            threadsByBot[thread.bot_id].push(thread.thread_id);
          });

          console.log('🔍 DEBUG: Organized threads by bot:', threadsByBot);
          setUserThreads(threadsByBot);
        } else {
          console.warn('🔍 DEBUG: No user ID found');
        }
      } catch (error) {
        console.error("Error fetching threads:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Fetch user's custom characters (for editing/management)
  const fetchCharacters = async () => {
    if (user) {
      try {
        const userId = getUserId(user);
        if (userId) {
          const characters = await listUserCharacters(userId);
          setUserCharacters(characters);
        }
      } catch (error) {
        console.error("Error fetching characters:", error);
      }
    }
  };

  // Fetch user's opened characters (for sidebar display)
  const fetchOpenedCharacters = async () => {
    if (user) {
      try {
        const userId = getUserId(user);
        if (userId) {
          const openedChars = await getUserOpenedCharacters(userId);
          console.log('Fetched opened characters:', openedChars);
          setOpenedCharacters(openedChars);
        }
      } catch (error) {
        console.error("Error fetching opened characters:", error);
      }
    }
  };

  // Update opened characters from props when they change
  useEffect(() => {
    if (userOpenedCharacters && userOpenedCharacters.length > 0) {
      console.log('Updating opened characters from props:', userOpenedCharacters);
      setOpenedCharacters(userOpenedCharacters);
    }
  }, [userOpenedCharacters]);

  useEffect(() => {
    fetchThreads();
    fetchCharacters();
    fetchOpenedCharacters();
  }, [user]);

  // Handle clicks outside the sidebar
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Function to update `isMobile` based on the screen size
  const checkIsMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  useEffect(() => {
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    // Cleanup event listener on unmount
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Remove the mobile check and always close sidebar
  const handleChatBotSelection = (productId) => {
    setSelectedChatBotId(productId);
    setIsOpen(false);
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const toggleBotExpansion = (botId) => {
    setExpandedBots((prev) => ({
      ...prev,
      [botId]: !prev[botId],
    }));
  };

  const handleThreadSelection = (botId, threadId) => {
    // Pass both the bot ID and thread ID
    setSelectedChatBotId(botId, threadId);
    // Save the selected thread to localStorage
    saveLastVisitedThread(botId, threadId);
    setIsOpen(false);
  };

  // Handle creating a new thread
  const handleCreateThread = async (e, botId) => {
    e.stopPropagation(); // Prevent triggering bot expansion
    if (!user) return;

    setCreatingThread(botId);
    try {
      const userId = getUserId(user);
      if (userId) {
        // Send botId without forcing conversion to prevent type issues
        const result = await createThread(userId, botId);
        if (result && result.thread_id) {
          // Refresh the thread list
          await fetchThreads();
          // Expand the bot to show the new thread
          setExpandedBots((prev) => ({
            ...prev,
            [botId]: true,
          }));
          // Select the new thread and save it as the last visited thread
          handleThreadSelection(botId, result.thread_id);
        }
      }
    } catch (error) {
      console.error("Error creating thread:", error);
    } finally {
      setCreatingThread(null);
    }
  };

  // Handle deleting a thread
  const [deletingThread, setDeletingThread] = useState(null);

  const handleDeleteThread = async (e, threadId) => {
    e.stopPropagation(); // Prevent triggering thread selection
    if (!user || !threadId) return;

    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this thread?")) {
      return;
    }

    setDeletingThread(threadId);
    try {
      const result = await deleteThread(threadId);
      if (result && result.deleted) {
        // Refresh the thread list
        await fetchThreads();

        // If the deleted thread was the selected one, clear the selection
        const lastVisited = getLastVisitedThread();
        if (lastVisited && lastVisited.threadId === threadId) {
          localStorage.removeItem("lastVisitedThread");
          // Redirect to the bot's main page without a specific thread
          setSelectedChatBotId(lastVisited.botId);
        }
      }
    } catch (error) {
      console.error("Error deleting thread:", error);
    } finally {
      setDeletingThread(null);
    }
  };

  // Handle creating a new character
  const handleCreateCharacter = async (characterData) => {
    if (!user) return;

    setCreatingCharacter(true);
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      const result = await createCharacter(characterData, userId);
      if (result && result.character_id) {
        // Refresh both character lists
        await fetchCharacters();
        await fetchOpenedCharacters();
        // Close the form
        setShowCharacterForm(false);
        // Optionally select the new character
        setSelectedChatBotId(result.character_id);
        setIsOpen(false);
        console.log("Character created successfully:", result.character_id);
        
        // Show success notification
        showNotification("Character created successfully! You can start chatting with your new character.", "success");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error creating character:", error);
      const errorMessage = error.message.includes("Failed to create character") 
        ? error.message 
        : "Failed to create character. Please check your connection and try again.";
      showNotification(errorMessage, "error");
    } finally {
      setCreatingCharacter(false);
    }
  };

  // Handle deleting a character
  const handleDeleteCharacter = async (e, characterId) => {
    e.stopPropagation(); // Prevent triggering character selection
    if (!user || !characterId) return;

    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this character? This action cannot be undone.")) {
      return;
    }

    setDeletingCharacter(characterId);
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      const result = await deleteCharacter(characterId, userId);
      if (result && result.deleted) {
        // Refresh both character lists
        await fetchCharacters();
        await fetchOpenedCharacters();
        
        // If the deleted character was the selected one, clear the selection
        const lastVisited = getLastVisitedThread();
        if (lastVisited && lastVisited.botId === characterId) {
          localStorage.removeItem("lastVisitedThread");
          // Redirect to the default bot
          setSelectedChatBotId("1"); // Default to Sam
        }
        
        console.log("Character deleted successfully:", characterId);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error deleting character:", error);
      const errorMessage = error.message.includes("Failed to delete character") 
        ? error.message 
        : "Failed to delete character. Please check your connection and try again.";
      alert(errorMessage);
    } finally {
      setDeletingCharacter(null);
    }
  };

  // Handle editing a character
  const handleEditCharacter = (e, characterId) => {
    e.stopPropagation(); // Prevent triggering character selection
    if (!user || !characterId) return;

    // Find the character data
    const character = userCharacters.find(c => c.character_id === characterId);
    if (!character) {
      console.error("Character not found:", characterId);
      showNotification("Character not found. Please refresh the page and try again.", "error");
      return;
    }

    // Set the character to edit and show the form
    setEditingCharacter(character);
    setShowEditForm(true);
  };

  // Handle updating a character
  const handleUpdateCharacter = async (characterId, characterData) => {
    if (!user || !characterId) return;

    setUpdatingCharacter(true);
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      const result = await updateCharacter(characterId, characterData, userId);
      if (result && result.updated) {
        // Refresh both character lists
        await fetchCharacters();
        await fetchOpenedCharacters();
        // Close the edit form
        setShowEditForm(false);
        setEditingCharacter(null);
        console.log("Character updated successfully:", characterId);
        
        // Show success notification
        showNotification("Character updated successfully! Changes will be reflected in new conversations.", "success");
      } else {
        throw new Error(result?.message || "Invalid response from server");
      }
    } catch (error) {
      console.error("Error updating character:", error);
      let errorMessage = "Failed to update character. Please check your connection and try again.";
      
      if (error.message.includes("You can only update your own characters")) {
        errorMessage = "Access denied: You can only edit characters you created.";
      } else if (error.message.includes("Failed to update character")) {
        errorMessage = error.message;
      }
      
      showNotification(errorMessage, "error");
    } finally {
      setUpdatingCharacter(false);
    }
  };

  // Handle canceling character edit
  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingCharacter(null);
    setUpdatingCharacter(false);
  };

  // Get character name for display
  const getCharacterName = (botId) => {
    if (isCustomCharacter(botId)) {
      // First check opened characters (prioritize for display)
      const openedCharsToUse = openedCharacters.length > 0 ? openedCharacters : userOpenedCharacters;
      const openedCharacter = openedCharsToUse.find(c => c.character_id === botId);
      if (openedCharacter) {
        return getCharacterDisplayName(openedCharacter);
      }
      
      // Fall back to user's owned characters (for editing)
      const character = userCharacters.find(c => c.character_id === botId);
      return character ? getCharacterDisplayName(character) : 'Unknown Character';
    }
    return chatbotNames[botId] || `ChatBot ${botId}`;
  };

  // Get character image for display
  const getCharacterImage = (botId) => {
    if (isCustomCharacter(botId)) {
      // First check opened characters (prioritize for display)
      const openedCharsToUse = openedCharacters.length > 0 ? openedCharacters : userOpenedCharacters;
      const openedCharacter = openedCharsToUse.find(char => char.character_id === botId);
      if (openedCharacter && openedCharacter.avatar_url) {
        return openedCharacter.avatar_url;
      }
      
      // Fall back to user's owned characters
      const character = userCharacters.find(char => char.character_id === botId);
      if (character && character.avatar_url) {
        return character.avatar_url;
      }
      
      // Final fallback to placeholder
      return "/placeholder.jpg";
    }
    return chatbotImages[botId];
  };

  // Helper function to determine character status for display
  const getCharacterStatus = (botId) => {
    if (!isCustomCharacter(botId)) {
      return "AI Sapiens"; // Built-in characters
    }
    
    // Check if this is a character owned by the user
    const isOwned = userCharacters.some(char => char.character_id === botId);
    
    if (isOwned) {
      return "My Character"; // User owns this character
    } else {
      return "Opened Character"; // Character opened from Discovery Board
    }
  };

  // Helper function to check if user owns a character (for showing edit/delete buttons)
  const isCharacterOwned = (botId) => {
    if (!isCustomCharacter(botId)) {
      return false; // Built-in characters are not "owned"
    }
    return userCharacters.some(char => char.character_id === botId);
  };

  // Get all available bots - under unified model, show user's opened characters
  const getAllAvailableBots = () => {
    // Always include built-in characters (they're always available under unified model)
    const builtInBots = subscribedProducts;
    
    // Add user's opened characters from the Character Discovery Board
    // Use local state first, fall back to props
    const openedCharsToUse = openedCharacters.length > 0 ? openedCharacters : userOpenedCharacters;
    const openedCharacterIds = openedCharsToUse.map(char => char.character_id);
    
    // Combine and deduplicate (remove subscription filtering - all opened characters are available)
    // Also filter out Jarvis (bot ID 0) completely
    const allBots = [...new Set([...builtInBots, ...openedCharacterIds])].filter(botId => botId !== "0" && botId !== 0);
    
    console.log('Available bots in sidebar (unified model):', {
      builtInBots,
      openedCharacterIds,
      totalOpened: openedCharsToUse.length,
      allBots
    });
    
    return allBots;
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={toggleSidebar}
        className="fixed top-5 left-4 text-xl text-white hover:text-gray-300 p-2 focus:outline-none z-50 transition-colors duration-200"
      >
        {isOpen ? <FaTimes /> : <FaBars />}
      </button>

      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full bg-gray-900 p-6 transition-transform duration-300 ease-in-out transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } w-80 z-[60] shadow-xl border-r border-gray-800 overflow-y-auto`}
      >
        <div className="mt-16">
          <h2 className="text-2xl font-semibold mb-6 text-white">
            AI Assistants
          </h2>

          {/* Todo List Link */}
          <Link
            to="/todos"
            className="flex items-center p-3 mb-4 rounded-lg transition-all duration-200 hover:bg-gray-800 text-white"
            onClick={() => setIsOpen(false)}
          >
            <FaListUl className="mr-3 text-blue-400" />
            <span>Todo Lists</span>
          </Link>

          <div className="border-t border-gray-700 my-4"></div>

          <div className="space-y-2">
            {getAllAvailableBots().length > 0 ? (
              getAllAvailableBots().map((botId) => (
                <div key={botId} className="mb-4">
                  <div className="flex items-center justify-between p-3 rounded-lg transition-all duration-200 hover:bg-gray-800">
                    <div
                      className="flex items-center space-x-3 cursor-pointer flex-1"
                      onClick={() => toggleBotExpansion(botId)}
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                        {getCharacterImage(botId) ? (
                          <img
                            src={getCharacterImage(botId)}
                            alt={getCharacterName(botId)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FaUser className="text-gray-400 text-lg" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-white">
                          {getCharacterName(botId)}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {getCharacterStatus(botId)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => handleCreateThread(e, botId)}
                        className="p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-white transition-colors duration-200"
                        title="Create new thread"
                        disabled={creatingThread === botId}
                      >
                        {creatingThread === botId ? (
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <FaPlus className="w-4 h-4" />
                        )}
                      </button>
                      {isCharacterOwned(botId) && (
                        <>
                          <button
                            onClick={(e) => handleEditCharacter(e, botId)}
                            className="p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-blue-500 transition-colors duration-200"
                            title="Edit character"
                            disabled={updatingCharacter || deletingCharacter === botId}
                          >
                            {updatingCharacter && editingCharacter?.character_id === botId ? (
                              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <FaEdit className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={(e) => handleDeleteCharacter(e, botId)}
                            className="p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-red-500 transition-colors duration-200"
                            title="Delete character"
                            disabled={deletingCharacter === botId || updatingCharacter}
                          >
                            {deletingCharacter === botId ? (
                              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <FaTrash className="w-4 h-4" />
                            )}
                          </button>
                        </>
                      )}
                      <button
                        className="p-2 text-gray-400 hover:text-white transition-colors duration-200"
                        onClick={() => toggleBotExpansion(botId)}
                        title="Expand/Collapse threads"
                      >
                        {expandedBots[botId] ? (
                          <FaChevronDown className="w-4 h-4" />
                        ) : (
                          <FaChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Thread List */}
                  {expandedBots[botId] && (
                    <div className="ml-6 mt-2 space-y-1 pl-7 border-l border-gray-700">
                      {isLoading ? (
                        <div className="text-gray-400 text-sm py-2">
                          Loading threads...
                        </div>
                      ) : userThreads[botId] &&
                        userThreads[botId].length > 0 ? (
                        userThreads[botId].map((threadId) => (
                          <div
                            key={threadId}
                            className="flex items-center justify-between py-2 px-3 text-sm text-gray-300 hover:bg-gray-800 rounded group"
                          >
                            <div
                              onClick={() =>
                                handleThreadSelection(botId, threadId)
                              }
                              className="cursor-pointer truncate flex-1"
                              title={threadId}
                            >
                              {threadId}
                            </div>
                            <button
                              onClick={(e) => handleDeleteThread(e, threadId)}
                              className="text-gray-500 hover:text-red-500 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              title="Delete thread"
                              disabled={deletingThread === threadId}
                            >
                              {deletingThread === threadId ? (
                                <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <FaTrash size={12} />
                              )}
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-400 text-sm py-2">
                          No threads found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-400">
                No available chatbots.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Character Creation Form Modal */}
      {showCharacterForm && (
        <CharacterCreationForm
          onCreateCharacter={handleCreateCharacter}
          onCancel={() => setShowCharacterForm(false)}
          isLoading={creatingCharacter}
        />
      )}

      {/* Character Edit Form Modal */}
      {showEditForm && editingCharacter && (
        <CharacterEditForm
          character={editingCharacter}
          onUpdateCharacter={handleUpdateCharacter}
          onCancel={handleCancelEdit}
          isLoading={updatingCharacter}
        />
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-[60] max-w-sm p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-600 text-white border border-green-500' 
            : 'bg-red-600 text-white border border-red-500'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              {notification.type === 'success' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="flex-shrink-0 ml-3 text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
